import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import {
  ErrorBox,
  Loader,
  PageHeader,
  StatusBadge,
  formatDateTime,
  formatDuration,
  formatPrice,
} from '../components/Common';
import { useAuthStore, isMasterRole } from '../store';
import { getWebApp } from '../telegram';

const ACTIVE = ['pending', 'confirmed', 'tentative'];

export function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.bookings.get(id!),
    enabled: !!id,
  });

  const cancel = useMutation({
    mutationFn: () => api.bookings.cancel(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });

  const confirm = useMutation({
    mutationFn: () => api.bookings.confirm(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
  const reject = useMutation({
    mutationFn: () => api.bookings.reject(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });

  if (isLoading) return <Loader />;
  if (error)
    return (
      <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />
    );
  if (!data) return null;

  const isClient = user?.id === data.client_id;
  const isOwnerMaster = user?.id === data.master_id && isMasterRole(user?.role);
  const canCancel = ACTIVE.includes(data.status);

  return (
    <div className="space-y-3">
      <PageHeader title="Запись" subtitle={formatDateTime(data.slot_start)} />

      <div className="card space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold">{data.service?.name ?? 'Услуга'}</p>
            {data.service && (
              <p className="text-xs text-tg-hint">
                {formatDuration(data.service.duration_minutes)} ·{' '}
                {formatPrice(data.service.price_min, data.service.price_max)}
              </p>
            )}
          </div>
          <StatusBadge status={data.status} />
        </div>

        <hr className="border-tg-hint/20" />

        <Row label="Когда" value={formatDateTime(data.slot_start)} />
        {data.master && (
          <Row
            label="Мастер"
            value={`${data.master.first_name} ${data.master.last_name ?? ''}`.trim()}
          />
        )}
        {isOwnerMaster && data.client && (
          <Row label="Клиент" value={`${data.client.first_name} ${data.client.last_name ?? ''}`.trim()} />
        )}
        {data.client_note && <Row label="Комментарий" value={data.client_note} />}
      </div>

      {(cancel.error || confirm.error || reject.error) && (
        <ErrorBox
          message={
            (cancel.error || confirm.error || (reject.error as Error))?.message ?? ''
          }
        />
      )}

      <div className="space-y-2">
        {isOwnerMaster && data.status === 'pending' && (
          <>
            <button
              className="btn-primary w-full"
              disabled={confirm.isPending}
              onClick={() => confirm.mutate()}
            >
              Подтвердить запись
            </button>
            <button
              className="btn-secondary w-full"
              disabled={reject.isPending}
              onClick={() => reject.mutate()}
            >
              Отклонить
            </button>
          </>
        )}

        {canCancel && (isClient || isOwnerMaster) && (
          <button
            className="btn-danger w-full"
            disabled={cancel.isPending}
            onClick={() => {
              const wa = getWebApp();
              const proceed = () => cancel.mutate();
              if (wa) wa.showConfirm('Точно отменить запись?', (ok) => ok && proceed());
              else if (window.confirm('Точно отменить запись?')) proceed();
            }}
          >
            {cancel.isPending ? 'Отменяем...' : 'Отменить запись'}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-tg-hint">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
